/**
 * useRewards — small helper over the progress-store so that any
 * activity grants a sticker and triggers the NEW reward animation
 * uniformly.
 *
 * `pending()` is the id of the freshly earned sticker to show in <RewardPopup>;
 * `award(id)` grants it and, only if it is new in the collection, puts it in
 * pending to animate it. Repeating an already rewarded activity does not re-animate.
 */

import { createSignal } from "solid-js";
import { createProgressStore } from "@melodimals/game-core";

/** Local day key `YYYY-MM-DD` (the child's device timezone). */
function localDayKey(date = new Date()): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function useRewards() {
  const store = createProgressStore();
  const [pending, setPending] = createSignal<string | null>(null);

  return {
    /** Grants the sticker; animates only if it is the first time it is earned. */
    award: (stickerId: string) => {
      // Marks "today" as a played day: feeds the friendly gamification streak
      // (pure projection over `Progress`). Local day key, idempotent.
      store.recordPlayDay(localDayKey());
      const { isNew } = store.awardSticker(stickerId);
      if (isNew) {
        setPending(stickerId);
      }
    },
    /** Id of the sticker to animate right now (or null). */
    pending,
    /** Closes the reward animation. */
    dismiss: () => setPending(null),
  };
}
